import { ChangeDetectionStrategy, ChangeDetectorRef, Component, effect, ElementRef, HostBinding, HostListener, inject, NgZone, OnDestroy, OnInit, untracked, ViewChild } from '@angular/core';
import { NgFor, NgIf } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { filter, Subscription } from 'rxjs';
import { HxAuthService } from 'hx-services';
import { HxToastrService } from 'hx-component';
import { environment } from '@callcenter-env/environment';
import { HeaderService } from '@callcenter-app/components/header/header.service';
import { CallStateService } from '@callcenter-app/service/call-state.service';
import { isOldWorkplaceUrl, WORKPLACE_READY } from '@callcenter-app/service/workplace.config';
import { SHELL_ICON } from '../shell-icons';
import { ShellUiService } from '../shell-ui.service';
import { THEME_MODES, ThemeMode, ThemeService } from '../theme.service';
import { ShellNotificationsComponent } from '../notifications/shell-notifications.component';

export const UI_LANGS = ['ru', 'kk', 'en'] as const;
const IS_MAC = /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);

/**
 * The header of the new shell (the canvas, «Шапка — строение и состояния»): the search of a client or an order (Ctrl K — «Заказы» with this search), the clock, «Тестовый сервер» off production, the operator number
 * (the window of `ShellNumberDialogComponent`), the language as three buttons, the theme and the bell. The rules of the number are those of the old header: the window opens by itself on the old workplace (stage 4), or
 * at the start when there is no number and stage 4 is off.
 */
@Component({
  selector: 'app-shell-header',
  templateUrl: './shell-header.component.html',
  styleUrls: ['./shell-header.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgIf, NgFor, FormsModule, TranslocoPipe, ShellNotificationsComponent],
})
export class ShellHeaderComponent implements OnInit, OnDestroy {
  @ViewChild('search') search?: ElementRef<HTMLInputElement>;
  @HostBinding('attr.data-theme') themeAttr = 'light';

  readonly icon = SHELL_ICON;
  readonly isProd = environment.production;
  readonly langs = UI_LANGS;
  readonly modes = THEME_MODES;
  readonly searchKey = IS_MAC ? '⌘K' : 'Ctrl K';
  query = '';
  time = '';
  date = '';
  slot = '';
  idsLoading = true;
  lang = 'ru';
  mode: ThemeMode = 'light';
  themeIcon: string = SHELL_ICON.sun;
  themeMenu = false;

  private readonly ui = inject(ShellUiService);
  private readonly theme = inject(ThemeService);
  private readonly auth = inject(HxAuthService);
  private readonly router = inject(Router);
  private readonly tr = inject(TranslocoService);
  private readonly toastr = inject(HxToastrService);
  private readonly network = inject(HeaderService);
  private readonly zone = inject(NgZone);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly el = inject(ElementRef<HTMLElement>);
  private readonly callState = inject(CallStateService, { optional: true });
  private clock?: ReturnType<typeof setInterval>;
  private subs = new Subscription();

  constructor() {
    effect(() => {
      this.themeAttr = this.theme.theme();
      this.mode = this.theme.mode();
      this.themeIcon = this.themeAttr === 'dark' ? SHELL_ICON.moon : SHELL_ICON.sun;
      this.idsLoading = this.ui.idsLoading();
      untracked(() => this.cdr.markForCheck());
    });
  }

  /** Ctrl K (⌘K on a Mac, where Ctrl+K deletes to the end of a line) — to the search from anywhere; the browser's own use of it gives way. */
  @HostListener('document:keydown', ['$event'])
  onKey(event: KeyboardEvent): void {
    const modifier = IS_MAC ? event.metaKey && !event.ctrlKey : event.ctrlKey && !event.metaKey;
    if (modifier && !event.altKey && !event.shiftKey && (event.key === 'k' || event.key === 'K' || event.code === 'KeyK')) {
      event.preventDefault();
      this.search?.nativeElement.focus();
      this.search?.nativeElement.select();
    } else if (event.key === 'Escape' && this.themeMenu) {
      event.preventDefault();
      this.themeMenu = false;
      this.cdr.markForCheck();
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (this.themeMenu && !this.el.nativeElement.querySelector('.hd__theme')?.contains(event.target as Node)) {
      this.themeMenu = false;
      this.cdr.markForCheck();
    }
  }

  ngOnInit(): void {
    this.lang = this.auth.getUiLang();
    this.tr.setActiveLang(this.lang);
    this.slot = this.auth.user.asteriskId ?? '';
    void this.ui.operatorIds().catch(() => undefined);
    this.tick();
    this.zone.runOutsideAngular(() => this.clock = setInterval(() => this.zone.run(() => this.tick()), 30_000));
    this.subs.add(this.network.network().subscribe(result => {
      const msg = `network.${result}`;
      if (result === 'online') {
        this.toastr.success(msg);
      } else {
        this.toastr.warning(msg);
      }
    }));
    this.subs.add(this.tr.langChanges$.subscribe(() => this.tick()));
    if (this.callState && WORKPLACE_READY) {
      // the number follows the one picked anywhere (the band, the workplace, the shift card) — no reload
      this.subs.add(this.callState.state$.subscribe(state => {
        this.slot = state.slot ?? '';
        this.cdr.markForCheck();
      }));
      // the window opens by itself on the old page only (stage 4): elsewhere the band, the card and the workplace offer the numbers
      this.subs.add(this.router.events.pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd)).subscribe(e => this.openOnOldPage(e.urlAfterRedirects)));
      this.openOnOldPage(this.router.url);
    } else if (!this.slot) {
      this.ui.openNumberDialog();
    }
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    if (this.clock) {
      clearInterval(this.clock);
    }
  }

  openMenu(): void {
    this.ui.openDrawer();
  }

  openNumber(): void {
    this.ui.openNumberDialog();
  }

  find(): void {
    const q = this.query.trim();
    if (!q) {
      return;
    }
    this.search?.nativeElement.blur();
    void this.router.navigate(['/orders'], { queryParams: { q } });
    this.query = '';
  }

  clearSearch(): void {
    this.query = '';
    this.search?.nativeElement.blur();
  }

  setLang(lang: string): void {
    if (lang === this.lang) {
      return;
    }
    const before = this.lang;
    this.lang = lang;
    // as the old switch: the language is the user's (the server keeps it), the page starts again in it; not kept — the pressed button goes back
    this.auth.setUiLang(lang).then(() => location.reload(), () => {
      this.lang = before;
      this.cdr.markForCheck();
    });
  }

  toggleThemeMenu(event: Event): void {
    event.stopPropagation();
    this.themeMenu = !this.themeMenu;
  }

  setMode(mode: ThemeMode): void {
    this.theme.setMode(mode);
    this.themeMenu = false;
  }

  private openOnOldPage(url: string): void {
    if (isOldWorkplaceUrl(url) && !this.slot) {
      this.ui.openNumberDialog();
    }
  }

  private tick(): void {
    const now = new Date();
    const locale = this.tr.getActiveLang() === 'kk' ? 'kk-KZ' : this.tr.getActiveLang() === 'en' ? 'en-GB' : 'ru-RU';
    this.time = now.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
    this.date = now.toLocaleDateString(locale, { weekday: 'short', day: 'numeric', month: 'long' });
    this.cdr.markForCheck();
  }
}
